// Legal Case Tracker on a phone (<1024px): view tabs + the filter bar as a bottom sheet + cards (Dean, 15 Oct 2026).
import { useNavigate } from "react-router-dom";
import { Scale, Loader2, AlarmClock, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { FilterBar } from "@/components/filters/FilterBar";
import { ViewTabs } from "@/components/filters/ViewTabs";
import { JourneyLine } from "@/components/collections/JourneyLine";
import { LivePill } from "../LivePill";

const fmtGBP = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => (iso ? new Date(iso.length > 10 ? iso : iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "");
const TONE = { breached: "bg-rose-600 text-white", red: "bg-red-50 text-red-900", amber: "bg-amber-50 text-amber-900", green: "bg-emerald-50 text-emerald-900", upcoming: "bg-slate-50 text-slate-700" };

export function PhoneLegalTracker({ lv, fields, cases, loading, summary, search, setSearch, includeHistory, setIncludeHistory }) {
  const navigate = useNavigate();
  return (
    <div className="p-3 space-y-3" data-testid="legal-case-tracker-phone">
      <div className="flex items-center gap-2">
        <Scale className="w-5 h-5 text-red-700" />
        <h1 className="text-base font-semibold">Legal Case Tracker</h1>
        <span className="ml-auto text-[11px] text-slate-500 tabular-nums" data-testid="phone-legal-count">{cases.length} files</span>
      </div>
      <div className="overflow-x-auto -mx-3 px-3"><ViewTabs lv={lv} allLabel="Live legal" testid="legal-views" className="flex-nowrap" /></div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search SF# / company / director…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9" data-testid="legal-search" />
      </div>
      <FilterBar fields={fields} tree={lv.tree} onChange={lv.setTree} testid="legal-filter">
        <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-slate-600" data-testid="legal-include-history">
          History <span className="tabular-nums" data-testid="legal-history-count">{summary.history_count || 0}</span>
          <Switch checked={includeHistory} onCheckedChange={setIncludeHistory} className="scale-75" data-testid="legal-include-history-switch" />
        </label>
      </FilterBar>
      {loading && cases.length === 0 ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        : cases.length === 0 ? <div className="py-10 text-center text-sm text-slate-500" data-testid="phone-legal-empty">No legal cases match.</div>
        : (
          <ul className="space-y-2" data-testid="legal-tracker-cards">
            {cases.map((c) => {
              const ne = c.next_event;
              const st = ne ? (ne.sla_breached ? "breached" : ne.priority || "upcoming") : null;
              return (
                <li key={c.case_id}>
                  <button type="button" onClick={() => navigate(`/legal/case/${c.case_id}?source=legal`)} className="w-full text-left border rounded-lg bg-white p-3 space-y-1.5 active:bg-slate-50 sf-tap" data-testid={`legal-case-row-${c.case_id}`}>
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm truncate">{c.customer_name || "—"}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{c.loan_application_id}{c.borrower_director_name ? ` · ${c.borrower_director_name}` : ""}</div>
                      </div>
                      <div className="text-sm font-semibold tabular-nums">{fmtGBP(c.balance_amount)}</div>
                    </div>
                    <JourneyLine journey={c.journey} caseId={c.case_id} />
                    <LivePill journey={c.journey} caseId={c.case_id} muted={!includeHistory} />
                    <div className="flex items-center gap-2 text-[11px] flex-wrap">
                      {ne ? (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${TONE[st]}`}><AlarmClock className="w-3 h-3" /> {ne.label} · {fmtDate(ne.date)}{ne.days_overdue > 0 ? ` · ${ne.days_overdue}d overdue` : ""}</span>
                      ) : <span className="italic text-red-700/80">No next action</span>}
                      <span className="ml-auto text-slate-500">{c.legal_handler ? `⚖ ${c.legal_handler}` : c.handler ? c.handler : "unassigned"}</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
    </div>
  );
}
